import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wand2, Image as ImageIcon, ArrowRight, LogOut } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";
import Monogram from "../components/Monogram";

const PRESETS = [
  { name: "founder-with-red-camera-v2", prompt: "Editorial cinematic photograph: the same Black man (clean-shaven bald head, beard) wearing a fitted matte black polo with the word 'MEDIA' printed in white sans-serif on the back, operating a RED cinema camera on a fluid head tripod in a dark studio. Softboxes glow behind him. Cinematic realism, deep blacks, 35mm Kodak Portra grade, subtle film grain. Aspect 3:2." },
  { name: "founder-coastal-drone", prompt: "Editorial cinematic photograph: the same Black man wearing a fitted matte black polo with 'MEDIA' on the back, piloting a DJI Inspire drone outdoors at dusk on a Maritime Canadian coastal cliff, slate-blue sky, dramatic light. Cinematic realism. Aspect 4:5 portrait." },
  { name: "founder-magazine-cover", prompt: "High-fashion editorial magazine cover portrait of the same Black man, three-quarter body against pure pitch-black studio background. Wearing a black polo with white 'MEDIA' lettering on the back partially visible over his shoulder. Strong rim light from right in deep crimson tone. Cinematic realism, 35mm grade." },
];

export default function AdminGeneratorPage() {
  const { token, user, logout } = useAuth();
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [generated, setGenerated] = useState([]);
  const [loading, setLoading] = useState(false);

  const onGenerate = async (e) => {
    e?.preventDefault();
    if (!name.trim() || prompt.trim().length < 20) {
      toast.error("Name and a detailed prompt (20+ chars) are required.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/generate-image`,
        { name: name.trim(), prompt: prompt.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setGenerated((prev) => [{ ...data, fullUrl, prompt }, ...prev]);
      toast.success("Image generated.");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === "string" ? detail : "Generation failed.");
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (p) => { setName(p.name); setPrompt(p.prompt); };

  return (
    <main className="min-h-screen bg-[#050505] text-ink-100" data-testid="page-admin-studio">
      <header className="border-b border-hair sticky top-0 z-40 bg-[#050505]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 py-4 flex items-center justify-between">
          <Monogram size={32} withWord />
          <nav className="flex items-center gap-6 text-[11px] tracking-[0.22em] uppercase">
            <Link to="/admin/leads" className="text-ink-300 hover:text-ink-100">Leads</Link>
            <Link to="/admin/blog" className="text-ink-300 hover:text-ink-100">Journal</Link>
            <Link to="/admin/studio" className="text-ink-100">Generator</Link>
            <Link to="/" className="text-ink-500 hover:text-ink-300">↗ Site</Link>
            <span className="hidden md:flex items-center gap-2 text-ink-500"><span className="w-1.5 h-1.5 rounded-full bg-rouge" />{user?.email}</span>
            <button onClick={logout} className="flex items-center gap-2 text-ink-300 hover:text-rouge"><LogOut size={14} /> Logout</button>
          </nav>
        </div>
      </header>

      <section className="mx-auto max-w-[1440px] px-6 lg:px-10 py-16">
        <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-ink-300">
          <span className="block w-8 h-px bg-rouge" /><span>AI Visual Generator · Nano Banana</span>
        </div>
        <h1 className="mt-4 font-serif-editorial text-5xl lg:text-6xl tracking-tight text-ink-100">Make visuals at the speed of an idea.</h1>
        <p className="mt-4 text-ink-300 text-sm max-w-2xl">Generate cinematic Phaneroo-grade images on demand. Pick a preset or write your own prompt — describe lighting, subject, scene and mood.</p>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <form onSubmit={onGenerate} className="lg:col-span-7 border border-hair p-8" data-testid="admin-gen-form">
            <label className="text-[10px] tracking-[0.24em] uppercase text-ink-500">Name (filename slug)</label>
            <input className="editorial-field" placeholder="founder-with-camera" value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-gen-name" />
            <label className="mt-6 text-[10px] tracking-[0.24em] uppercase text-ink-500 block">Prompt</label>
            <textarea rows={8} className="editorial-field resize-none" placeholder="Describe the scene, lighting, subject, mood…" value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="admin-gen-prompt" />
            <button type="submit" disabled={loading} data-testid="admin-gen-submit" className="mt-8 inline-flex items-center gap-3 bg-rouge hover:bg-rouge-deep disabled:opacity-60 text-ink-100 px-8 py-4 text-[11px] tracking-[0.24em] uppercase">
              <Wand2 size={14} /> {loading ? "Generating…" : "Generate"}
              <ArrowRight size={14} />
            </button>
          </form>

          <div className="lg:col-span-5 space-y-3">
            <div className="text-[10px] tracking-[0.24em] uppercase text-ink-500 mb-2">Presets</div>
            {PRESETS.map((p) => (
              <button key={p.name} onClick={() => applyPreset(p)} className="w-full text-left border border-hair p-4 hover:border-ink-100 transition-colors" data-testid={`admin-preset-${p.name}`}>
                <div className="text-ink-100 text-sm">{p.name}</div>
                <div className="text-ink-500 text-xs mt-1 line-clamp-2">{p.prompt}</div>
              </button>
            ))}
          </div>
        </div>

        {!!generated.length && (
          <div className="mt-20">
            <div className="text-[11px] tracking-[0.24em] uppercase text-ink-300 mb-6">Recently generated</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {generated.map((g) => (
                <motion.div key={g.name} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="border border-hair bg-[#0c0c0e]" data-testid={`admin-gen-card-${g.name}`}>
                  <div className="aspect-square overflow-hidden bg-[#070708] flex items-center justify-center">
                    <img src={g.fullUrl} alt={g.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <div className="text-ink-100 text-sm flex items-center gap-2"><ImageIcon size={12} className="text-rouge" />{g.name}</div>
                    <a href={g.fullUrl} target="_blank" rel="noreferrer" className="text-ink-500 text-xs hover:text-ink-100 break-all block mt-2">{g.fullUrl}</a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
